import React, { useEffect, useState } from 'react'
import {
  Button,
  Select,
  MenuItem,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Tooltip
} from '@mui/material'
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Help as HelpIcon
} from '@mui/icons-material'
import { 
  CustomDefinition, 
  CustomMetricSource, 
  CustomMetricValueType, 
  PartyRole
} from '../../../interfaces/db'
import { 
  isDefinitionValid, 
  COLORS, 
  decodeDescriptions, 
  DescriptionPair, 
  defaultDefinition, 
  formatDescriptionForValueType 
} from '../utils'
import { EditModalProps } from '../types'
import { MODAL_STYLES } from '../constants'
import { Typography } from '../../../interfaces/typography'

export function EditModal({ open, definition, definitions, codeOptions, onClose, onSave, onTest }: EditModalProps) {
  const [editedDef, setEditedDef] = useState<CustomDefinition>(defaultDefinition);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [descriptions, setDescriptions] = useState<DescriptionPair[]>([]);

  useEffect(() => {
    if (definition) {
      setEditedDef(definition);
      setDescriptions(decodeDescriptions(definition));
    } else {
      setEditedDef(defaultDefinition);
      setDescriptions([]);
    }
  }, [definition]);

  // Set defaults for new metrics
  useEffect(() => {
    if (definition && definition.custom_definition_id.startsWith('temp_') && definition.label === '') {
      // This is a new metric, set defaults
      updateDef({
        value_source: CustomMetricSource.GEN_AI,
        value_type: CustomMetricValueType.PERCENT,
        value_gen_str: definition.description || '',
        value_gen_desc_str: '0:,50:,100:'
      });
    }
  }, [definition]);

  const updateDef = (updates: Partial<CustomDefinition>) => {
    setEditedDef(prev => ({ ...prev, ...updates }));
  };

  const updateDescription = (category: string, description_to_update: string) => {
    setDescriptions(prev => {
      const description = description_to_update.replaceAll('\t', '')
      const newDescriptions = prev.map(d => 
        d.category === category ? { ...d, description } : d
      );
      
      // If the category doesn't exist, add it
      if (!prev.find(d => d.category === category)) {
        newDescriptions.push({ category, description });
      }

      // Update the value_gen_desc_str in editedDef
      const newValue = formatDescriptionForValueType(editedDef, newDescriptions);
      updateDef({ value_gen_desc_str: newValue });

      return newDescriptions;
    });
  };

  const handleSave = () => {
    if (!editedDef) return
    if (editedDef.value_type === CustomMetricValueType.ENUM) {
      const categories = editedDef.value_type_enum_str?.replaceAll('\t', '').split(',').map(c => c.trim()) || []
      if (categories.some(c => !c)) {
        setValidationError('Categories cannot be empty')
        return
      }
    }

    const validation = isDefinitionValid(editedDef)
    if (!validation.valid) {
      setValidationError(validation.reason)
      return
    }

    onSave(editedDef)
    onClose()
  }

  const handleTest = () => {
    if (!editedDef) return
    
    const validation = isDefinitionValid(editedDef)
    if (!validation.valid) {
      setValidationError(validation.reason)
      return
    }
    
    if (onTest) {
      onTest(editedDef)
    }
  }

  const isMetricValid = editedDef && isDefinitionValid(editedDef).valid

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: MODAL_STYLES.paper }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 1.5,
        mb: 2
      }}>
        <Typography variant="h3" color="#1F2937" fontWeight={600}>
          {definition?.custom_definition_id ? 'Edit Metric' : 'Create New Metric'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pb: 2 }}>
        {/* Show notice if metric has been invalidated */}
        {validationError && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 1,
              mb: 2
            }}
          >
            {validationError}
          </Alert>
        )}

        {/* Main Configuration Section */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: 2,
          p: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          {/* Left Column - Basic Info */}
          <Stack spacing={1.5}>
            <Typography variant="h4" color="#3B82F6" fontWeight={600} className="text-sm">
              Basic Information
            </Typography>
            <Box sx={MODAL_STYLES.fieldWrapper}>
              <TextField
                required
                label="Metric Label"
                value={editedDef.label || ''}
                onChange={(e) => updateDef({ label: e.target.value })}
                fullWidth
                error={!editedDef.label || editedDef.label.includes(',')}
                helperText={editedDef.label?.includes(',') ? 'Label cannot contain commas' : ''}
                sx={MODAL_STYLES.input}
              />
              <Tooltip title="Give your metric a clear name that describes what it measures" placement="right">
                <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={MODAL_STYLES.fieldWrapper}>
              <TextField
                required={editedDef.value_source === CustomMetricSource.GEN_AI}
                label="Description"
                value={editedDef.description || ''}
                onChange={(e) => {
                  const newDescription = e.target.value
                  const newUpdates: Partial<CustomDefinition> = { description: newDescription }

                  // If the source is GEN_AI, also copy the description to value_gen_str
                  if (editedDef.value_source === CustomMetricSource.GEN_AI) {
                    newUpdates.value_gen_str = newDescription
                  }

                  updateDef(newUpdates)
                }}
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                placeholder="Explain what this metric measures"
                sx={{
                  ...MODAL_STYLES.input,
                  '& .MuiInputBase-root': {
                    height: 'auto'
                  },
                  '& .MuiOutlinedInput-inputMultiline': {
                    overflow: 'auto',
                    maxHeight: '300px'
                  }
                }}
              />
              <Tooltip title="Provide a detailed explanation of what this metric represents" placement="right">
                <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>

          {/* Right Column - Calculation Method */}
          <Stack spacing={1.5}>
            <Typography variant="h4" color="#3B82F6" fontWeight={600} className="text-sm">
              Calculation Method
            </Typography>
            <Box sx={MODAL_STYLES.fieldWrapper}>
              <FormControl required fullWidth sx={MODAL_STYLES.input}>
                <InputLabel>Value Source</InputLabel>
                <Select
                  value={editedDef.value_source}
                  label="Value Source"
                  onChange={(e) => {
                    const source = e.target.value as CustomMetricSource
                    let updates: Partial<CustomDefinition> = {
                      value_source: source,
                      // Clear all source-specific fields first
                      value_gen_str: null,
                      value_gen_desc_str: null,
                      value_phrase_str: null,
                      value_phrase_role: null,
                      value_average_str: null,
                      value_code_str: null
                    }

                    switch (source) {
                      case CustomMetricSource.GEN_AI:
                        updates.value_type = editedDef.value_type || CustomMetricValueType.PERCENT
                        updates.value_gen_str = editedDef.description // Set description as the gen prompt
                        if (updates.value_type === CustomMetricValueType.BOOLEAN) {
                          updates.value_gen_desc_str = 'yes:,no:'
                        } else if (updates.value_type === CustomMetricValueType.PERCENT) {
                          updates.value_gen_desc_str = '0:,50:,100:'
                        } else if (updates.value_type === CustomMetricValueType.ENUM) {
                          updates.value_type_enum_str = ''
                          updates.value_gen_desc_str = ''
                        }
                        break
                      case CustomMetricSource.PHRASE:
                        updates.value_type = CustomMetricValueType.BOOLEAN
                        break
                      case CustomMetricSource.AVERAGE:
                        updates.value_type = CustomMetricValueType.PERCENT
                        break
                      case CustomMetricSource.CODE:
                        // Set appropriate value type based on first selected code
                        if (codeOptions.length > 0) {
                          updates.value_type = codeOptions[0].output_type === 'PERCENTAGE' 
                            ? CustomMetricValueType.PERCENT 
                            : codeOptions[0].output_type === 'BOOLEAN'
                              ? CustomMetricValueType.BOOLEAN
                              : CustomMetricValueType.ENUM
                        }
                        break
                    }
                    
                    updateDef(updates)
                  }}
                >
                  <MenuItem value={CustomMetricSource.GEN_AI}>Generative AI</MenuItem>
                  <MenuItem value={CustomMetricSource.PHRASE}>Phrase Match</MenuItem>
                  <MenuItem value={CustomMetricSource.AVERAGE}>Average Score</MenuItem>
                  {codeOptions.length > 0 && (
                    <MenuItem value={CustomMetricSource.CODE}>Custom Code</MenuItem>
                  )}
                </Select>
              </FormControl>
              <Tooltip title="Choose how this metric will be calculated" placement="right">
                <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {editedDef.value_source === CustomMetricSource.GEN_AI && (
              <Box sx={MODAL_STYLES.fieldWrapper}>
                <FormControl required fullWidth sx={MODAL_STYLES.input}>
                  <InputLabel>Value Type</InputLabel>
                  <Select
                    value={editedDef.value_type}
                    label="Value Type"
                    onChange={(e) => {
                      const valueType = e.target.value as CustomMetricValueType
                      let updates: Partial<CustomDefinition> = {
                        value_type: valueType,
                        value_type_enum_str: null,
                        value_gen_desc_str: null
                      }

                      if (editedDef.value_source === CustomMetricSource.GEN_AI) {
                        updates.value_gen_str = editedDef.description // Set description as the gen prompt
                        if (valueType === CustomMetricValueType.BOOLEAN) {
                          updates.value_gen_desc_str = 'yes:,no:'
                        } else if (valueType === CustomMetricValueType.PERCENT) {
                          updates.value_gen_desc_str = '0:,50:,100:'
                        } else if (valueType === CustomMetricValueType.ENUM) {
                          updates.value_type_enum_str = ''
                        }
                      }
                      
                      updateDef(updates)
                    }}
                  >
                    <MenuItem value={CustomMetricValueType.BOOLEAN}>Yes/No</MenuItem>
                    <MenuItem value={CustomMetricValueType.PERCENT}>Percentage</MenuItem>
                    <MenuItem value={CustomMetricValueType.TEXT}>Text</MenuItem>
                    <MenuItem value={CustomMetricValueType.ENUM}>Categorical</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Select the type of value this metric will produce" placement="right">
                  <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {editedDef.value_source === CustomMetricSource.CODE && (
              <Box sx={MODAL_STYLES.fieldWrapper}>
                <FormControl required fullWidth sx={MODAL_STYLES.input}>
                  <InputLabel>Select Codes</InputLabel>
                  <Select
                    multiple
                    value={editedDef.value_code_str ? editedDef.value_code_str.split(',') : []}
                    label="Select Codes"
                    onChange={(e) => {
                      const selectedCodes = e.target.value as string[]
                      updateDef({ 
                        value_code_str: selectedCodes.join(','),
                        // Update value_type based on the first selected code if any are selected
                        ...(selectedCodes.length > 0 && {
                          value_type: codeOptions.find(co => co.name === selectedCodes[0])?.output_type === 'PERCENTAGE'
                            ? CustomMetricValueType.PERCENT
                            : codeOptions.find(co => co.name === selectedCodes[0])?.output_type === 'BOOLEAN'
                              ? CustomMetricValueType.BOOLEAN 
                              : CustomMetricValueType.ENUM
                        })
                      })
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => {
                          const code = codeOptions.find(co => co.name === value)
                          return (
                            <Chip 
                              key={value} 
                              label={value}
                              size="small"
                              sx={{ 
                                backgroundColor: code?.output_type === 'PERCENTAGE' 
                                  ? 'primary.50'
                                  : code?.output_type === 'BOOLEAN'
                                    ? 'success.50'
                                    : 'warning.50',
                                borderColor: code?.output_type === 'PERCENTAGE'
                                  ? 'primary.200'
                                  : code?.output_type === 'BOOLEAN'
                                    ? 'success.200'
                                    : 'warning.200',
                                color: code?.output_type === 'PERCENTAGE'
                                  ? 'primary.700'
                                  : code?.output_type === 'BOOLEAN'
                                    ? 'success.700'
                                    : 'warning.700',
                              }}
                            />
                          )
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            py: 1,
                            px: 2,
                            borderRadius: 1,
                            mx: 1,
                            my: 0.5,
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'primary.100',
                              '&:hover': {
                                backgroundColor: 'primary.200',
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    {codeOptions.map((code) => (
                      <MenuItem 
                        key={code.name} 
                        value={code.name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Chip
                          size="small"
                          label={code.output_type === 'PERCENTAGE' ? 'Percent' : code.output_type === 'BOOLEAN' ? 'Yes/No' : 'Category'}
                          sx={{
                            backgroundColor: code.output_type === 'PERCENTAGE' 
                              ? 'primary.50'
                              : code.output_type === 'BOOLEAN'
                                ? 'success.50'
                                : 'warning.50',
                            borderColor: code.output_type === 'PERCENTAGE'
                              ? 'primary.200'
                              : code.output_type === 'BOOLEAN'
                                ? 'success.200'
                                : 'warning.200',
                            color: code.output_type === 'PERCENTAGE'
                              ? 'primary.700'
                              : code.output_type === 'BOOLEAN'
                                ? 'success.700'
                                : 'warning.700',
                            minWidth: 80
                          }}
                        />
                        <Typography>{code.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Select the codes to use for this metric" placement="right">
                  <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Output Configuration - only show if there's content */}
        {editedDef.value_type !== CustomMetricValueType.TEXT && editedDef.value_source !== CustomMetricSource.CODE && (
          (editedDef.value_source === CustomMetricSource.GEN_AI || 
           editedDef.value_source === CustomMetricSource.PHRASE || 
           editedDef.value_source === CustomMetricSource.AVERAGE) && (
          <Box sx={{ 
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {editedDef.value_source === CustomMetricSource.GEN_AI && (
              <>
                {editedDef.value_type === CustomMetricValueType.BOOLEAN && (
                  <Stack spacing={1.5}>
                    <Box sx={MODAL_STYLES.fieldWrapper}>
                      <TextField
                        required
                        label="Description for 'Yes'"
                        value={descriptions.find(d => d.category === 'yes')?.description || ''}
                        onChange={(e) => updateDescription('yes', e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={20}
                        sx={MODAL_STYLES.input}
                      />
                    </Box>

                    <Box sx={MODAL_STYLES.fieldWrapper}>
                      <TextField
                        required
                        label="Description for 'No'"
                        value={descriptions.find(d => d.category === 'no')?.description || ''}
                        onChange={(e) => updateDescription('no', e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={20}
                        sx={MODAL_STYLES.input}
                      />
                    </Box>
                  </Stack>
                )}

                {editedDef.value_type === CustomMetricValueType.PERCENT && (
                  <Stack spacing={1.5}>
                    <Box sx={MODAL_STYLES.fieldWrapper}>
                      <TextField
                        required
                        label="Description for Low (<40%)"
                        value={descriptions.find(d => d.category === 'Low (<40%)')?.description || ''}
                        onChange={(e) => updateDescription('Low (<40%)', e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={20}
                        sx={MODAL_STYLES.input}
                      />
                    </Box>

                    <Box sx={MODAL_STYLES.fieldWrapper}>
                      <TextField
                        required
                        label="Description for Middle (40-70%)"
                        value={descriptions.find(d => d.category === 'Middle (40-70%)')?.description || ''}
                        onChange={(e) => updateDescription('Middle (40-70%)', e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={20}
                        sx={MODAL_STYLES.input}
                      />
                    </Box>

                    <Box sx={MODAL_STYLES.fieldWrapper}>
                      <TextField
                        required
                        label="Description for High (>70%)"
                        value={descriptions.find(d => d.category === 'High (>70%)')?.description || ''}
                        onChange={(e) => updateDescription('High (>70%)', e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={20}
                        sx={MODAL_STYLES.input}
                      />
                    </Box>
                  </Stack>
                )}

                {editedDef.value_type === CustomMetricValueType.ENUM && (
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5" color="#3B82F6" fontWeight={600}>
                          Categories
                        </Typography>
                        <Tooltip title="Add categories and their descriptions to define what each option means. The AI will choose one of these categories based on the conversation." placement="right">
                          <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                            <HelpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const categories = editedDef.value_type_enum_str === null || editedDef.value_type_enum_str === ''
                            ? []
                            : editedDef.value_type_enum_str.replaceAll('\t', '').split(',')
                          const descriptions = editedDef.value_gen_desc_str === null || editedDef.value_gen_desc_str === ''
                            ? []
                            : editedDef.value_gen_desc_str.split(',')

                          // Use a placeholder name instead of an empty string
                          const newCategoryName = `New Category ${categories.length + 1}`
                          categories.push(newCategoryName)
                          descriptions.push(`${newCategoryName}:`)

                          updateDef({
                            value_type_enum_str: categories.join(','),
                            value_gen_desc_str: descriptions.join(' ')
                          })
                        }}
                        size="small"
                        sx={{ 
                          textTransform: 'none',
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.50' }
                        }}
                      >
                        Add Category
                      </Button>
                    </Box>

                    {editedDef.value_type_enum_str && editedDef.value_type_enum_str.replaceAll('\t', '').split(',')
                      .map((category, index) => {
                        return (
                          <Box key={index} sx={{ ...MODAL_STYLES.fieldWrapper, mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'start', mb: 1 }}>
                              <TextField
                                required
                                placeholder="Category name"
                                value={category}
                                error={!category.trim()}
                                helperText={!category.trim() ? "Category name cannot be empty" : ""}
                                onChange={(e) => {
                                  const newCategory = e.target.value
                                  const oldDescription = descriptions.find(d => d.category === category)?.description || ''
                                  const newDescriptions = descriptions.map(d => 
                                    d.category === category 
                                      ? { category: newCategory, description: oldDescription }
                                      : d
                                  )
                                  const categories = editedDef.value_type_enum_str?.replaceAll('\t', '').split(',') || []
                                  categories[index] = newCategory
                                  
                                  setDescriptions(newDescriptions)
                                  updateDef({
                                    value_type_enum_str: categories.join(','),
                                    value_gen_desc_str: newDescriptions.map(d => `${d.category}:${d.description}`).join(' ')
                                  })
                                }}
                                size="small"
                                sx={{ ...MODAL_STYLES.input, flex: 1, minWidth: 200 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const categories = editedDef.value_type_enum_str?.replaceAll('\t', '').split(',') || []
                                  categories.splice(index, 1)
                                  const newDescriptions = descriptions.filter(d => d.category !== category)
                                  
                                  setDescriptions(newDescriptions)
                                  updateDef({
                                    value_type_enum_str: categories.join(','),
                                    value_gen_desc_str: newDescriptions.map(d => `${d.category}:${d.description}`).join(' ')
                                  })
                                }}
                                sx={{ 
                                  mt: 0.5,
                                  color: 'error.main',
                                  '&:hover': { 
                                    backgroundColor: 'error.50' 
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <TextField
                              label={`Description for '${category}'`}
                              value={descriptions.find(d => d.category === category)?.description || ''}
                              onChange={(e) => updateDescription(category, e.target.value)}
                              fullWidth
                              multiline
                              minRows={2}
                              maxRows={8}
                              sx={MODAL_STYLES.input}
                            />
                          </Box>
                        )
                      })}
                  </Stack>
                )}
              </>
            )}

            {editedDef.value_source === CustomMetricSource.PHRASE && (
              <Stack spacing={1.5}>
                <Box sx={MODAL_STYLES.fieldWrapper}>
                  <TextField
                    required
                    label="Phrases to Match"
                    value={editedDef.value_phrase_str || ''}
                    onChange={(e) => updateDef({ value_phrase_str: e.target.value })}
                    fullWidth
                    placeholder="Enter phrases separated by commas"
                    helperText="Example: thank you for resolving, issue fixed, problem solved"
                    sx={MODAL_STYLES.input}
                  />
                  <Tooltip title="Specify phrases to look for in the conversation" placement="right">
                    <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={MODAL_STYLES.fieldWrapper}>
                  <FormControl fullWidth sx={MODAL_STYLES.input}>
                    <InputLabel>Speaker Role</InputLabel>
                    <Select
                      value={
                        editedDef.value_phrase_role == null
                          ? "ANYONE"
                          : editedDef.value_phrase_role
                      }
                      label="Speaker Role"
                      onChange={(e) => {
                        const newRole =
                          e.target.value === "ANYONE" ? null : (e.target.value as PartyRole)
                        updateDef({ value_phrase_role: newRole })
                      }}
                    >
                      <MenuItem value="ANYONE">Anyone</MenuItem>
                      <MenuItem value={PartyRole.REP}>Rep</MenuItem>
                      <MenuItem value={PartyRole.PROSPECT}>Prospect</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Choose which speaker's messages to search for phrases" placement="right">
                    <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
            )}

            {editedDef.value_source === CustomMetricSource.AVERAGE && (
              <Box sx={MODAL_STYLES.fieldWrapper}>
                <FormControl required fullWidth sx={MODAL_STYLES.input}>
                  <InputLabel>Metrics to Average</InputLabel>
                  <Select
                    multiple
                    value={editedDef.value_average_str ? editedDef.value_average_str.split(',') : []}
                    label="Metrics to Average"
                    onChange={(e) => {
                      const value = (e.target.value as string[]).join(',')
                      updateDef({ value_average_str: value })
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => {
                          const metric = definitions.find(d => d.metric_idx !== null && d.metric_idx.toString() === value)
                          return (
                            <Chip 
                              key={value} 
                              label={metric?.label || `Metric ${value}`}
                              size="small"
                            />
                          )
                        })}
                      </Box>
                    )}
                  >
                    {definitions
                      .filter(d => 
                        d.custom_definition_id !== editedDef.custom_definition_id && 
                        d.metric_idx !== null &&
                        [CustomMetricValueType.BOOLEAN, CustomMetricValueType.PERCENT].includes(d.value_type)
                      )
                      .map((d) => (
                        <MenuItem key={d.metric_idx} value={d.metric_idx?.toString() || ''}>
                          {d.label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Tooltip title="Select the metrics you want to average together" placement="right">
                  <IconButton size="small" sx={MODAL_STYLES.helpIcon}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          )
        )}

      </DialogContent>

      <DialogActions sx={{ 
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1.5
      }}>
        <Button 
          onClick={onClose}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            color: 'text.secondary'
          }}
        >
          Cancel
        </Button>
        
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isMetricValid && (
            <Button 
              variant="outlined"
              onClick={handleTest}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                borderColor: '#3B82F6',
                color: '#3B82F6',
                '&:hover': {
                  borderColor: '#2563EB',
                  backgroundColor: '#EFF6FF'
                }
              }}
            >
              Test
            </Button>
          )}
          
          <Button 
            variant="contained"
            onClick={handleSave}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              py: 1,
              backgroundImage: COLORS.primary.gradient,
              '&:hover': {
                backgroundImage: COLORS.primary.gradient,
                filter: 'brightness(0.9)'
              }
            }}
          >
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
} 